import React, { useState, useEffect } from 'react';
import { 
  PhoneOutlined, 
  FileTextOutlined, 
  ClockCircleOutlined,
  CheckOutlined,
  ReloadOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListCheck } from '@fortawesome/free-solid-svg-icons';
import {
  TaskHeader,
  TaskFilters,
  TaskFilterButton,
  TaskList,
  TaskCard,
  TaskIcon,
  TaskContent,
  TaskCheckbox,
  RefreshButton,
  EmptyState
} from './styled';
import DialerService from '../../services/DialerService';

// Format date for display
const formatDueDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const taskDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  const diffDays = Math.floor((taskDate - today) / (1000 * 60 * 60 * 24));
  const timeStr = date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  if (diffDays === 0) {
    return `Today at ${timeStr}`;
  } else if (diffDays === 1) {
    return `Tomorrow at ${timeStr}`;
  } else if (diffDays === -1) {
    return `Yesterday at ${timeStr}`;
  } else if (diffDays < -1) {
    return `${Math.abs(diffDays)} days overdue`;
  } else if (diffDays < 7) {
    return `${date.toLocaleDateString('en-US', { weekday: 'long' })} at ${timeStr}`;
  }
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

// Check if task is overdue
const isOverdue = (dateString) => {
  return new Date(dateString) < new Date();
};

const TasksPanel = ({ onSelectNumber }) => {
  const [filter, setFilter] = useState('today'); // 'today' | 'overdue' | 'all'
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingTasks, setCompletingTasks] = useState({});
  
  // Fetch tasks
  const fetchTasks = async () => {
    setLoading(true);
    try {
      const taskList = await DialerService.getTasks({ status: 'pending' });
      setTasks(taskList);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Initial fetch
  useEffect(() => {
    fetchTasks();
  }, []);
  
  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const taskDate = new Date(task.dueDate);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    switch (filter) {
      case 'today':
        return taskDate >= today && taskDate < tomorrow;
      case 'overdue':
        return taskDate < now;
      case 'all':
      default:
        return true;
    }
  });
  
  // Handle task completion
  const handleComplete = async (taskId) => {
    setCompletingTasks(prev => ({ ...prev, [taskId]: true }));
    
    try {
      await DialerService.completeTask(taskId);
      // Remove from list
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (error) {
      console.error('Error completing task:', error);
    } finally {
      setCompletingTasks(prev => ({ ...prev, [taskId]: false }));
    }
  };
  
  // Handle task click (for callback tasks)
  const handleTaskClick = (task) => {
    if (task.type === 'callback' && task.contactPhone && onSelectNumber) {
      const digits = task.contactPhone.replace(/\D/g, '');
      onSelectNumber({
        number: digits,
        countryCode: '+1',
        contactName: task.contactName
      });
    }
  };
  
  return (
    <div>
      <TaskHeader>
        <div className="title">
          <FontAwesomeIcon icon={faListCheck} />
          Tasks
        </div>
        <RefreshButton onClick={fetchTasks} title="Refresh tasks">
          {loading ? <LoadingOutlined spin /> : <ReloadOutlined />}
        </RefreshButton>
      </TaskHeader>
      
      <TaskFilters>
        <TaskFilterButton
          $active={filter === 'today'}
          onClick={() => setFilter('today')}
        >
          Today
        </TaskFilterButton>
        <TaskFilterButton
          $active={filter === 'overdue'}
          onClick={() => setFilter('overdue')}
        >
          Overdue
        </TaskFilterButton>
        <TaskFilterButton
          $active={filter === 'all'}
          onClick={() => setFilter('all')}
        >
          All
        </TaskFilterButton>
      </TaskFilters>
      
      <TaskList>
        {loading ? (
          <EmptyState>
            <LoadingOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <div className="title">Loading tasks...</div>
          </EmptyState>
        ) : filteredTasks.length === 0 ? (
          <EmptyState>
            <CheckOutlined style={{ fontSize: 32, marginBottom: 12, color: '#52c41a' }} />
            <div className="title">
              {filter === 'today' && 'No tasks for today'}
              {filter === 'overdue' && 'No overdue tasks'}
              {filter === 'all' && 'No pending tasks'}
            </div>
            <div className="subtitle">
              {filter === 'today' && 'You\'re all caught up!'}
              {filter === 'overdue' && 'Great job staying on top of things'}
              {filter === 'all' && 'Create a task from the Contact tab'}
            </div>
          </EmptyState>
        ) : (
          filteredTasks.map((task) => {
            const overdue = isOverdue(task.dueDate);
            const completing = completingTasks[task.id];
            
            return (
              <TaskCard
                key={task.id}
                $overdue={overdue}
                onClick={() => handleTaskClick(task)}
              >
                <TaskIcon $type={task.type}>
                  {task.type === 'callback' ? <PhoneOutlined /> : <FileTextOutlined />}
                </TaskIcon>
                <TaskContent $overdue={overdue}>
                  <div className="title">{task.title}</div>
                  <div className="due-date">
                    <ClockCircleOutlined />
                    {formatDueDate(task.dueDate)}
                  </div>
                </TaskContent>
                <TaskCheckbox
                  $checked={false}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleComplete(task.id);
                  }}
                  disabled={completing}
                  title="Mark as complete"
                >
                  {completing ? <LoadingOutlined spin /> : null}
                </TaskCheckbox>
              </TaskCard>
            );
          })
        )}
      </TaskList>
    </div>
  );
};

export default TasksPanel;
